import { UserError } from '@brainfocus/web/utils/assert';
import { isSessionStale } from '@brainfocus/web/services/session_identity';

/**
 * Display given message for short period.
 *
 * Nothing is displayed once the window is stale (#871, PR #896 review). Every
 * request from such a window is refused, and the application refreshes itself
 * on its own — a view refetches whenever the window is brought to the front —
 * so each refusal added another banner behind the notice, all of them repeating
 * what the notice already says. The notice is the single statement of what
 * happened; banners printed BEFORE it appeared are left alone, since the notice
 * paints over them anyway.
 */
export function flash(msg: string, category?: 'error' | 'warning') {
    if (isSessionStale()) {
        return;
    }
    const container = document.getElementById('bf_flash_container');
    if (container) {
        const flashElement = document.createElement('div');
        flashElement.classList.add('bf-flash-card');
        if (category) {
            flashElement.classList.add('bf-flash-cat-' + category);
        }
        flashElement.textContent = msg;
        container.appendChild(flashElement);
        setFlashTimeout(flashElement);
    }
}

/**
 * Longest technical detail that may be appended to a user-facing message. A
 * banner is a message, not a document: anything longer is a payload that leaked
 * into an error string (#865).
 */
export const MAX_ERROR_DETAIL_LENGTH = 200;

/**
 * Anything that looks like markup — `<!doctype`, `<html`, `<div …` — is a
 * response body, not a message.
 */
const MARKUP_PATTERN = /<[a-zA-Z!/]/;

/**
 * Reduce the technical part of an error to something safe to render, or `null`
 * when it must not be rendered at all.
 *
 * A failed API call used to surface the server's whole response in the banner:
 * a login page complete with `FLASK_CSRF` token, feature flags, changelog JSON
 * and bundle paths (#865). Escaping is not the fix — `flash` already writes
 * through `textContent` — so the rule is to reject bodies outright and cap
 * whatever is left. The full error always remains available in the console.
 */
export function sanitizeErrorDetail(detail: string): string | null {
    const trimmed = detail.trim();
    if (!trimmed || MARKUP_PATTERN.test(trimmed)) {
        return null;
    }
    if (trimmed.length > MAX_ERROR_DETAIL_LENGTH) {
        return `${trimmed.slice(0, MAX_ERROR_DETAIL_LENGTH)}…`;
    }
    return trimmed;
}

/**
 * Log given error and display summary with the messgae to the user.
 *
 * This is the product's ONE error surface. Nothing reports a failure through a
 * native `window.alert`: that dialog carries the browser's own chrome (the host
 * name, "This page says…") and blocks the page, so the same failure reported
 * through both surfaces read as two different products (#865).
 */
export function flashError(msg: string, e: unknown) {
    console.error(e);
    if (e instanceof UserError) {
        flash(e.message, 'error');
        return;
    }
    const detail = sanitizeErrorDetail(e instanceof Error ? e.message : String(e));
    flash(detail ? `${msg}: ${detail}` : msg, 'error');
}

/**
 * Enable hiding of the flash cards created using flask's flash function.
 */
export function enableFlaskFlashes(): void {
    let timeToRead = 0;
    document.querySelectorAll('.bf-flash-card').forEach((el) => {
        timeToRead += setFlashTimeout(el, timeToRead) + 1000;
    });

    window.addEventListener('error', (e) => {
        if (e && 'message' in e) {
            if (isBenignBrowserWarning(e.message)) return;
            flash(e.message, 'error');
        }
    });
}

/**
 * Some browser warnings surface as window `error` events even though they are
 * harmless and outside our control. The "ResizeObserver loop …" notice fires
 * when a resize callback (e.g. the vtk.js medical viewer re-rendering on a
 * layout reflow) doesn't settle within one frame; it has no functional effect.
 * We must not flash these to the user as errors.
 */
export function isBenignBrowserWarning(message: string): boolean {
    return message.startsWith('ResizeObserver loop');
}

function setFlashTimeout(flashElement: Element, preTime: number = 0): number {
    const text = flashElement.textContent;
    const MIN_DISPLAY_TIME = 10000; // Minimum 10 seconds to ensure users can read important messages
    const timeToRead = Math.max((text?.length ?? 0) * 170, MIN_DISPLAY_TIME) + preTime;
    const timeoutRef = setTimeout(() => {
        hideFlash(flashElement);
    }, timeToRead);
    flashElement.addEventListener('click', () => {
        clearTimeout(timeoutRef);
        hideFlash(flashElement);
    });
    return timeToRead;
}

function hideFlash(flashElement: Element): void {
    flashElement.classList.add('bf-flash-card-hidden');
    setTimeout(() => {
        flashElement.parentNode?.removeChild(flashElement);
    }, 700);
}
